import remove from "lodash/remove"
import { defineStore } from "pinia"

import http from "../services/http"
import storage from "../services/storage"
import type { SubscriptionInfo, UserProfile } from "../types/profile"

interface ICurrentUser {
  admin: boolean
  any_subscription_active_till: string
  billing_address: any
  business_address: any
  business_name: string
  business_number: any
  can_downgrade_subscriptions: boolean
  cancelled_subscription: {
    plan: any
    active_till: any
  }
  contact_email: string
  cookie_consent: string
  email: string
  grade_info: any
  group_info: any
  has_active_inspire: boolean
  has_individual_subscription: boolean
  has_test_works: boolean
  id: number
  image: any
  interests_list: string[]
  journals_autosync_accepted: boolean
  lessons_allowed: boolean
  lessons_allowed_or_active_school: boolean
  mykoob_school_id: any
  name: string
  new_version_available: boolean
  show_active_worksheets: boolean
  plan: string
  profile_emoji: string
  provider: string
  role: any
  school_id: number | null
  school_info: string
  school_payment: {
    active_school: boolean
    active_till: string
    school_assigned_plan: string
  }
  smart_books_allowed: boolean
  subscription_paid_till: any
  subscription_plan: string
  surname: string
  unread_notifications_count: number
  user_grade_slug: string
  user_type: string
  voucher: {
    has_voucher: any
    voucher_active_till: any
    voucher_plan: any
  }
  whitelisted: boolean
  worksheets_allowed: boolean
}

interface ILogin extends IErrorAndSuccessCallbacks {
  email: string
  password: string
}

interface ILoginWithProvider extends IErrorAndSuccessCallbacks {
  provider: string
  code: string
  callback?: string | null
}

export interface IUserState {
  busy: boolean
  current: ICurrentUser | null
  module: string
  authToken: string | null
  notifications: any[]
  total: number | null
  subjects: any[]
  upgradeNoticeVisible: boolean
  upgradeNoticeWithRedirectHome: boolean
  asyncInitReloadOnceDone: boolean
  profile: UserProfile | null
  subscription: SubscriptionInfo | null
}

interface ISubjectBookmarkParams extends IErrorAndSuccessCallbacks {
  subjectId: number | null
}

interface ICookieConsentParams extends IErrorAndSuccessCallbacks {
  cookieConsent: string | null
}

let sessionRefresh: Promise<void> | null = null

// In-app destinations go through the router instead of a second app boot.
const inAppPath = (url: string) => {
  const base = useRuntimeConfig().app.baseURL.replace(/\/$/, "")
  const path = url.startsWith(window.location.origin) ? url.slice(window.location.origin.length) : url

  return path.startsWith(`${base}/`) ? path.slice(base.length) : null
}

const redirectAfterAuth = () => {
  const afterAuthUrl = storage.get("after_auth_url")
  storage.remove("after_auth_url")

  if (!afterAuthUrl) {
    return navigateTo("/")
  }

  const path = inAppPath(afterAuthUrl)
  if (path) {
    return navigateTo(path)
  }

  window.location.href = afterAuthUrl
}

export const useUserStore = defineStore("userStore", {
  state: (): IUserState => ({
    busy: true,
    module: "normal",
    authToken: null,
    current: null,
    notifications: [],
    total: null,
    subjects: [],
    upgradeNoticeVisible: false,
    upgradeNoticeWithRedirectHome: false,
    asyncInitReloadOnceDone: false,
    profile: null,
    subscription: null,
  }),

  getters: {
    name(state) {
      return state.current?.name || "..."
    },

    hasToken(state) {
      return !!state.authToken
    },

    authenticated(state) {
      return !!state.authToken
    },

    canSendToEklase(state) {
      return state.current?.user_type === "teacher" && state.current?.provider === "eklase" && state.current?.lessons_allowed_or_active_school
    },

    isTeacherType(state) {
      return state.current?.user_type === "teacher"
    },

    isTeacher(state) {
      if (!state.current?.lessons_allowed_or_active_school) {
        return false
      }
      return state.current.user_type === "teacher"
    },

    isTeacherWithNoLessons(state) {
      return state.current?.user_type === "teacher" && !state.current?.lessons_allowed_or_active_school
    },

    isTeacherWithLessons(state) {
      if (!state.current?.lessons_allowed_or_active_school) {
        return false
      }

      if (["eliis"].includes(state.current?.provider ?? "")) {
        // disable journals and lessons for eliis teachers
        return false
      }

      return state.current.user_type === "teacher"
    },

    isTeacherWithActiveSchoolAndProPlan(state) {
      return state.current?.user_type === "teacher" &&
        state.current?.school_payment.active_school &&
        state.current?.school_payment.school_assigned_plan === "pro"
    },

    isStudentWithActiveSchoolAndProPlan(state) {
      return state.current?.user_type === "student" &&
        state.current?.school_payment.active_school &&
        state.current?.school_payment.school_assigned_plan === "pro"
    },

    isContentCreatorOrBetter(state) {
      return ["admin", "content_admin", "content_creator", "field_leader"].includes(state.current?.role ?? "")
    },

    hasAdminOrContentAdminRole(state) {
      return state.current?.role === "admin" || state.current?.role === "content_admin"
    },

    isEklaseUser(state) {
      if (!state.current?.provider) {
        return false
      }

      return state.current.provider === "eklase"
    },

    isStudent(state) {
      return state.current?.user_type === "student"
    },

    showActiveWorksheets(state) {
      return state.current?.show_active_worksheets
    },

    isElder(state) {
      return state.current?.user_type === "elder"
    },

    isOther(state) {
      return state.current?.user_type === "other"
    },

    isOtherOrFree(state) {
      return state.current?.user_type === "other" || state.current?.plan === "free"
    },
  },

  actions: {
    async init() {
      const storedModule = storage.getAuthModule()
      if (storedModule) {
        this.module = storedModule
      }

      this.initFromStorage()

      if (this.authToken) {
        await this.asyncReloadOnce()
      }

      this.busy = false
    },

    initFromStorage() {
      const storedAuthToken = storage.getAuthToken()
      if (storedAuthToken) {
        this.authToken = storedAuthToken
        this.current = storage.getCurrentUser()
      }
    },

    reload(
      { successCallback = null, errorCallback = null }: IErrorAndSuccessCallbacks = {
        successCallback: null,
        errorCallback: null,
      }
    ) {
      http.put({
        url: "/v2/user-session/plain",
        body: {},
        successCallback: (context) => {
          this.current = context.response._data.user
          storage.setCurrentUser(this.current)
          this.busy = false
          if (successCallback) {
            successCallback(context)
          }
        },
        errorCallback: (context) => {
          if (errorCallback) {
            errorCallback(context)
          }
          this.busy = false
        },
      })
    },

    async asyncReloadOnce() {
      if (this.asyncInitReloadOnceDone) {
        return
      }

      if (!sessionRefresh) {
        sessionRefresh = (async () => {
          try {
            const response = (await http.put({
              url: "/v2/user-session/plain",
            })) as { success: boolean; user: ICurrentUser }

            if (response.success) {
              this.current = response.user
              storage.setCurrentUser(this.current)
              this.asyncInitReloadOnceDone = true
            }
          } catch (e) {
            // NOOP
          }
        })().finally(() => {
          sessionRefresh = null
        })
      }

      await sessionRefresh
    },

    setModule(module: string) {
      this.module = module
      storage.setAuthModule(module)
    },

    logout() {
      const shouldRedirectToLogin = () => {
        const router = useRouter()
        const currentPath = router.currentRoute.value.path
        const config = useRuntimeConfig()
        const configHost = new URL(config.public.landingDomainName)
        if (window.location.hostname !== configHost.hostname && (["/", "/pirmsskola", "/abonements"].includes(currentPath))) {
          return false
        }

        return true
      }

      if (this.authToken) {
        http.delete({
          url: "/v2/user-session",
          extras: { skipUnauthorizedRedirect: true },
          successCallback: () => {
            if (shouldRedirectToLogin()) {
              navigateTo("/login")
            }
          },
          errorCallback: () => {
            if (shouldRedirectToLogin()) {
              navigateTo("/login")
            }
          },
        })
      }

      storage.clearAuthToken()
      storage.clearCurrentUser()
      storage.clearSmartTopicsGradeSelect()
      this.authToken = null
      this.current = null
      this.profile = null
    },

    login({ email, password, successCallback = null, errorCallback = null }: ILogin) {
      http.post({
        url: "/v2/user-session/plain",
        body: { email, password },
        extras: { skipUnauthorizedRedirect: true },
        successCallback: (context) => {
          this.authToken = context.response._data.auth_token
          this.current = context.response._data.user
          this.profile = null
          storage.setAuthToken(this.authToken)
          storage.setCurrentUser(this.current)
          // window.destroyBeacon()
          // window.initBeacon()
          if (successCallback) {
            successCallback(context)
            redirectAfterAuth()
          }
        },
        errorCallback,
      })
    },

    loginWithProvider({ provider, code, callback = null, successCallback = null, errorCallback = null }: ILoginWithProvider) {
      http.post({
        url: `/v2/user-session/${provider}`,
        body: callback ? { code, callback } : { code },
        extras: { skipUnauthorizedRedirect: true },
        successCallback: (context) => {
          this.authToken = context.response._data.auth_token
          this.current = context.response._data.user
          this.profile = null
          storage.setAuthToken(this.authToken)
          storage.setCurrentUser(this.current)
          successCallback && successCallback(context)
          redirectAfterAuth()
        },
        errorCallback,
      })
    },

    requestPasswordReset({ email, successCallback = null, errorCallback = null }) {
      http.post({
        url: "/v2/password-resets",
        body: { email },
        successCallback: (context) => {
          if (successCallback) {
            successCallback(context)
          }
        },
        errorCallback,
      })
    },

    fetchSubjects(
      { successCallback = null, errorCallback = null }: IErrorAndSuccessCallbacks = {
        successCallback: null,
        errorCallback: null,
      }
    ) {
      http.get({
        url: "v2/user/subjects",
        successCallback: (context) => {
          this.subjects = context.response._data.subjects
          successCallback && successCallback(context)
        },
        errorCallback: (context) => {
          errorCallback && errorCallback(context)
        },
      })
    },

    addSubjectBookmark(
      { subjectId, successCallback, errorCallback }: ISubjectBookmarkParams = {
        subjectId: null,
        successCallback: null,
        errorCallback: null,
      }
    ) {
      http.post({
        url: "v2/user/subjects",
        body: {
          subject_id: subjectId,
        },
        successCallback: (context) => {
          this.subjects.push(context.response._data.subject)
          successCallback && successCallback(context)
        },
        errorCallback: (context) => {
          errorCallback && errorCallback(context)
        },
      })
    },

    removeSubjectBookmark(
      { subjectId, successCallback, errorCallback }: ISubjectBookmarkParams = {
        subjectId: null,
        successCallback: null,
        errorCallback: null,
      }
    ) {
      http.delete({
        url: "v2/user/subjects",
        body: {
          subject_id: subjectId,
        },
        successCallback: (context) => {
          remove(this.subjects, (subject) => subject.id === context.response._data.subject.id)
          successCallback && successCallback(context)
        },
        errorCallback: (context) => {
          errorCallback && errorCallback(context)
        },
      })
    },

    updateCookieConsent(
      { cookieConsent, successCallback, errorCallback }: ICookieConsentParams = {
        cookieConsent: null,
        successCallback: null,
        errorCallback: null,
      }
    ) {
      http.put({
        url: "v2/user/profile/update_cookie_consent",
        body: {
          user: {
            cookie_consent: cookieConsent,
          },
        },
        successCallback: (context) => {
          this.current = context.response._data.user
          storage.setCurrentUser(this.current)
          successCallback && successCallback(context)
        },
        errorCallback: (context) => {
          errorCallback && errorCallback(context)
        },
      })
    },

    async fetchProfile(): Promise<UserProfile> {
      const response = (await http.get({
        url: "v2/user/profile",
      })) as { success: boolean; user: UserProfile }

      this.profile = response.user
      return response.user
    },

    async updateProfile(changes: Partial<Pick<UserProfile, "name" | "surname" | "user_type" | "profile_emoji" | "school_info" | "grade_info">>): Promise<UserProfile> {
      const response = (await http.put({
        url: "v2/user/profile",
        body: { user: changes },
      })) as { success: boolean; user: UserProfile }

      this.profile = response.user
      if (this.current && changes.profile_emoji !== undefined) {
        this.current.profile_emoji = response.user.profile_emoji
      }
      return response.user
    },

    async activateVoucher(code: string): Promise<{ success: boolean; message: string }> {
      try {
        const data = (await http.post({
          url: "v2/user/voucher-checkout",
          body: { code },
        })) as { success: boolean; message: string }
        return data
      } catch (e: any) {
        const body = e?.data || e?.response?._data || {}
        return { success: false, message: body.message || "Kļūda" }
      }
    },

    async fetchSubscription(): Promise<SubscriptionInfo> {
      const response = (await http.get({
        url: "v2/user/subscription",
      })) as { success: boolean } & SubscriptionInfo

      const subscription: SubscriptionInfo = {
        has_subscription: response.has_subscription,
        valid_until: response.valid_until,
        plan: response.plan,
      }
      this.subscription = subscription
      return subscription
    },
  },
})

// Must migrate to PINIA usage!
export default {
  // changeModule() {
  //   if (this.module() === "normal") {
  //     this.state.module = "special"
  //   } else {
  //     this.state.module = "normal"
  //   }
  //   storage.set(constants.AUTH_MODULE, this.state.module)
  // },
  // has_subscription() {
  //   if (!this.authenticated()) {
  //     return false
  //   }
  //   return (
  //     this.state.current.status === "active" ||
  //     this.state.current.status === "cancelled" ||
  //     this.state.current.has_trial ||
  //     this.state.current.user_type === "whitelisted"
  //   )
  // },
  // remote_logout(successCallback, errorCallback) {
  //   http.delete(
  //     "user-session",
  //     {},
  //     (response) => {
  //       if (response.data.success) {
  //         if (successCallback) successCallback(response)
  //       } else {
  //         if (errorCallback) errorCallback(response)
  //       }
  //     },
  //     errorCallback
  //   )
  // },
  // confirm(code, successCallback = null, errorCallback = null) {
  //   http.post(
  //     "email-confirmations",
  //     { token: code },
  //     (response) => {
  //       if (successCallback) {
  //         successCallback(response)
  //       }
  //     },
  //     errorCallback
  //   )
  // },
  // reset_password(token, params, successCallback = null, errorCallback = null) {
  //   http.put(
  //     `password-resets/${token}`,
  //     params,
  //     (response) => {
  //       if (successCallback) {
  //         successCallback(response)
  //       }
  //     },
  //     errorCallback
  //   )
  // },
  // fetchNotifications() {
  //   this.state.notifications = []
  //   http.get("user/notifications", {}, (response) => {
  //     this.state.notifications = response.data.notifications
  //     this.state.current.unread_notifications_count = 0
  //     storage.set("user:current", this.state.current)
  //   })
  // },
  // deleteAccount(successCallback = null, errorCallback = null) {
  //   http.delete(
  //     "user/profile",
  //     {},
  //     (response) => {
  //       if (successCallback) {
  //         successCallback(response)
  //       }
  //     },
  //     errorCallback
  //   )
  // },
  // register(params, successCallback, errorCallback) {
  //   http.post(
  //     "user-registrations",
  //     params,
  //     (response) => {
  //       if (successCallback) {
  //         successCallback(response)
  //       }
  //     },
  //     errorCallback
  //   )
  // },
  // update_plan(new_plan) {
  //   this.state.current.plan = new_plan
  //   storage.set("user:current", this.state.current)
  // },
  // update_subscription_plan(subscription_plan) {
  //   this.state.current.subscription_plan = subscription_plan
  //   storage.set("user:current", this.state.current)
  // },
  // update_subscription_paid_till(subscription_paid_till) {
  //   this.state.current.subscription_paid_till = subscription_paid_till
  //   storage.set("user:current", this.state.current)
  // },
  // update_voucher(voucher) {
  //   this.state.current.voucher = voucher
  //   storage.set("user:current", this.state.current)
  // },
  // update_school_payment(school_payment) {
  //   this.state.current.school_payment = school_payment
  //   storage.set("user:current", this.state.current)
  // },
  // update_cancelled_subscription(cancelled_subscription) {
  //   this.state.current.cancelled_subscription = cancelled_subscription
  //   storage.set("user:current", this.state.current)
  // },
  // fetch_plan(successCallback) {
  //   http.get("user/subscription", {}, (response) => {
  //     this.update_plan(response.data.plan)
  //     this.update_subscription_plan(response.data.subscription_plan)
  //     this.update_subscription_paid_till(response.data.subscription_paid_till)
  //     this.update_voucher(response.data.voucher)
  //     this.update_school_payment(response.data.school_payment)
  //     this.update_cancelled_subscription(response.data.cancelled_subscription)
  //     if (successCallback) {
  //       successCallback()
  //     }
  //   })
  // },
  // activateVoucher(code, successCallback, errorCallback) {
  //   http.post(
  //     "user/voucher-checkout",
  //     { code: code },
  //     (response) => {
  //       if (successCallback) {
  //         successCallback()
  //       }
  //     },
  //     errorCallback
  //   )
  // },
  // update_profile(params, successCallback, errorCallback) {
  //   http.put(
  //     "user/profile",
  //     params,
  //     (response) => {
  //       this.state.current = response.data.user
  //       storage.set("user:current", this.state.current)
  //       if (successCallback) {
  //         successCallback(response)
  //       }
  //     },
  //     errorCallback
  //   )
  // },
  // update_profile_emoji(params, successCallback, errorCallback) {
  //   http.put(
  //     "user/profile/update_profile_emoji",
  //     params,
  //     (response) => {
  //       this.state.current = response.data.user
  //       storage.set("user:current", this.state.current)
  //       if (successCallback) {
  //         successCallback(response)
  //       }
  //     },
  //     errorCallback
  //   )
  // },
  // login_facebook(code, callback, successCallback = null, errorCallback = null) {
  //   http.post(
  //     "user-session/facebook",
  //     { code, callback },
  //     (response) => {
  //       this.state.authToken = response.data.authToken
  //       this.state.current = response.data.user
  //       storage.set(constants.AUTH_TOKEN_KEY, this.state.authToken)
  //       storage.set("user:current", this.state.current)
  //       window.initBeacon()
  //       if (successCallback) {
  //         successCallback(response)
  //       }
  //     },
  //     errorCallback
  //   )
  // },
  // login_eklase(code, callback, successCallback = null, errorCallback = null) {
  //   http.post(
  //     "user-session/eklase",
  //     { code, callback },
  //     (response) => {
  //       this.state.authToken = response.data.authToken
  //       this.state.current = response.data.user
  //       storage.set(constants.AUTH_TOKEN_KEY, this.state.authToken)
  //       storage.set("user:current", this.state.current)
  //       window.initBeacon()
  //       if (successCallback) {
  //         successCallback(response)
  //       }
  //     },
  //     errorCallback
  //   )
  // },
  // login_draugiem(code, successCallback = null, errorCallback = null) {
  //   http.post(
  //     "user-session/draugiem",
  //     { code },
  //     (response) => {
  //       this.state.authToken = response.data.authToken
  //       this.state.current = response.data.user
  //       storage.set(constants.AUTH_TOKEN_KEY, this.state.authToken)
  //       storage.set("user:current", this.state.current)
  //       window.initBeacon()
  //       if (successCallback) {
  //         successCallback(response)
  //       }
  //     },
  //     errorCallback
  //   )
  // },
  // login_mykoob(code, successCallback = null, errorCallback = null) {
  //   http.post(
  //     "user-session/mykoob",
  //     { code },
  //     (response) => {
  //       this.state.authToken = response.data.authToken
  //       this.state.current = response.data.user
  //       storage.set(constants.AUTH_TOKEN_KEY, this.state.authToken)
  //       storage.set("user:current", this.state.current)
  //       window.initBeacon()
  //       if (successCallback) {
  //         successCallback(response)
  //       }
  //     },
  //     errorCallback
  //   )
  // },
  // getPlanInfo(name) {
  //   var subscription_plan = this.state.current.subscription_plan
  //   var cancelled_subscription = this.state.current.cancelled_subscription
  //   var voucher = this.state.current.voucher
  //   var school_payment = this.state.current.school_payment
  //   var type, till
  //   if (subscription_plan !== "free" && subscription_plan === name) {
  //     type = "subscription"
  //     till = this.state.current.subscription_paid_till
  //   }
  //   if (
  //     cancelled_subscription &&
  //     cancelled_subscription.plan &&
  //     cancelled_subscription.plan !== school_payment.school_assigned_plan &&
  //     cancelled_subscription.plan === name
  //   ) {
  //     type = "cancelled"
  //     till = cancelled_subscription.active_till
  //   }
  //   if (voucher && voucher.has_voucher && voucher.voucher_plan === name) {
  //     type = "voucher"
  //     till = voucher.voucher_active_till
  //   }
  //   if (
  //     school_payment.active_school &&
  //     school_payment.school_assigned_plan !== "free" &&
  //     school_payment.school_assigned_plan === name
  //   ) {
  //     type = "school"
  //     till = school_payment.active_till
  //   }
  //   return {
  //     type: type,
  //     till: till,
  //   }
  // },
  // isSchoolAuth() {
  //   return ["mykoob", "eklase"].includes(this.state.current.provider)
  // },
  // updateContactEmail(email, successCallback, errorCallback) {
  //   http.put(
  //     "user/profile/contact_email",
  //     { email: email },
  //     () => {
  //       this.state.current.contact_email = email
  //       if (successCallback) {
  //         successCallback()
  //       }
  //     },
  //     errorCallback && errorCallback()
  //   )
  // },
}
